import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, BadgeCheck } from 'lucide-react';
import { SEO, SchemaOrg } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatUSD = (n) =>
  typeof n === 'number'
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)
    : '—';

const VERDICT_STYLES = {
  Buy: 'text-[#2F6B3F] border-[#2F6B3F]',
  Hold: 'text-[#8B5A2B] border-[#8B5A2B]',
  Avoid: 'text-[#9B2C2C] border-[#9B2C2C]',
};

const PriceChart = ({ history }) => {
  if (!history || history.length < 2) return null;
  const values = history.map((p) => p.price);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const w = 800;
  const h = 300;
  const pad = 40;
  const innerW = w - pad * 2;
  const innerH = h - pad * 2;
  const step = innerW / (history.length - 1);

  const path = values
    .map((v, i) => {
      const x = (pad + i * step).toFixed(2);
      const y = (pad + innerH - ((v - min) / range) * innerH).toFixed(2);
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');

  const areaPath = `${path} L ${pad + innerW} ${pad + innerH} L ${pad} ${pad + innerH} Z`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
      {/* Horizontal grid */}
      {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
        const y = pad + innerH * t;
        const value = max - range * t;
        return (
          <g key={i}>
            <line x1={pad} y1={y} x2={pad + innerW} y2={y} stroke="#E5E0D8" strokeWidth="0.5" />
            <text x={pad - 10} y={y + 4} fontSize="11" textAnchor="end" fill="#888" fontFamily="DM Sans">
              ${Math.round(value / 100) / 10}K
            </text>
          </g>
        );
      })}
      {/* X axis labels */}
      {history.map((p, i) => {
        if (history.length > 8 && i % 2 !== 0) return null;
        const x = pad + i * step;
        return (
          <text key={i} x={x} y={h - 10} fontSize="11" textAnchor="middle" fill="#888" fontFamily="DM Sans">
            {p.date?.slice(0, 4)}
          </text>
        );
      })}
      <path d={areaPath} fill="#C69C6D" fillOpacity="0.1" />
      <path d={path} fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* Latest point */}
      {(() => {
        const last = history.length - 1;
        const x = pad + last * step;
        const y = pad + innerH - ((values[last] - min) / range) * innerH;
        return <circle cx={x} cy={y} r="5" fill="#C69C6D" stroke="#FAF9F6" strokeWidth="2" />;
      })()}
    </svg>
  );
};

export const InvestmentDetailPage = () => {
  const { slug } = useParams();
  const [piece, setPiece] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/investments/${slug}`);
        if (!cancelled) setPiece(res.data);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    window.scrollTo(0, 0);
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-6 h-6 border border-[#111] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !piece) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF9F6]">
        <p className="overline overline-gold mb-4">404</p>
        <h1 className="heading-editorial text-4xl mb-8">Investment Piece Not Found</h1>
        <Link to="/investments" className="btn-editorial">Back to Index</Link>
      </div>
    );
  }

  const appreciationUSD = piece.current_resale - piece.baseline_price;
  const appreciationPct = ((appreciationUSD / piece.baseline_price) * 100).toFixed(1);

  return (
    <div data-testid={`investment-detail-${slug}`} className="bg-[#FAF9F6]">
      <SEO
        title={`${piece.name} — Pre-Owned Resale Value & Investment Outlook`}
        description={`${piece.name} pre-owned price tracker: current resale ${formatUSD(piece.current_resale)}, retail ${formatUSD(piece.retail_new)}, ${piece.cagr > 0 ? '+' : ''}${piece.cagr.toFixed(1)}% ten-year CAGR. Editorial verdict: ${piece.verdict}.`}
        url={`/investments/${piece.slug}`}
        image={piece.image_url}
        type="article"
      />
      <SchemaOrg
        type="breadcrumb"
        data={{
          items: [
            { name: 'Home', url: '/' },
            { name: 'Investment Index', url: '/investments' },
            { name: piece.name, url: `/investments/${piece.slug}` },
          ],
        }}
      />
      {/* Product + Review + AggregateRating — derives star rating from CAGR.
          CAGR > 5% → 5.0, > 3% → 4.5, > 1% → 4.0, > 0% → 3.5, > -2% → 3.0, else → 2.0 */}
      {(() => {
        const cagr = piece.cagr ?? 0;
        const ratingValue = cagr > 5 ? 5.0 : cagr > 3 ? 4.5 : cagr > 1 ? 4.0 : cagr > 0 ? 3.5 : cagr > -2 ? 3.0 : 2.0;
        return (
          <SchemaOrg
            type="product"
            data={{
              name: piece.name,
              description: piece.thesis,
              image: piece.image_url,
              brand: piece.designer,
              category: 'Pre-Owned Luxury Jewelry',
              url: `/investments/${piece.slug}`,
              price: piece.current_resale,
              priceCurrency: 'USD',
              review: { body: piece.thesis, ratingValue },
              rating: { ratingValue, ratingCount: 1, reviewCount: 1 },
            }}
          />
        );
      })()}

      {/* Editorial Hero */}
      <section className="border-b border-[#E5E0D8]">
        <div className="container-editorial py-12 lg:py-20">
          <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-[#888] mb-10">
            <Link to="/" className="hover:text-[#111] transition-colors">Home</Link>
            <span>/</span>
            <Link to="/investments" className="hover:text-[#111] transition-colors">Investment Index</Link>
            <span>/</span>
            <span className="text-[#111]">{piece.designer}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="aspect-square overflow-hidden img-zoom">
              <img src={piece.image_url} alt={piece.name} className="w-full h-full object-cover" />
            </div>

            <div>
              <p className="overline overline-gold mb-4">{piece.designer}</p>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-[#111] mb-6">
                {piece.name}
              </h1>
              <p className="text-[#555] leading-relaxed mb-10">{piece.material}</p>

              <div className="grid grid-cols-2 gap-px bg-[#E5E0D8] border border-[#E5E0D8]">
                <div className="bg-white p-6">
                  <p className="text-[10px] tracking-wider uppercase text-[#888] mb-2">Current Resale</p>
                  <p className="font-heading text-3xl font-light text-[#111]">{formatUSD(piece.current_resale)}</p>
                </div>
                <div className="bg-white p-6">
                  <p className="text-[10px] tracking-wider uppercase text-[#888] mb-2">Retail (New)</p>
                  <p className="font-heading text-3xl font-light text-[#555]">{formatUSD(piece.retail_new)}</p>
                </div>
                <div className="bg-white p-6">
                  <p className="text-[10px] tracking-wider uppercase text-[#888] mb-2">{piece.history?.length ? `Since ${piece.baseline_year}` : 'Baseline'}</p>
                  <p className={`font-heading text-3xl font-light ${appreciationUSD >= 0 ? 'text-[#2F6B3F]' : 'text-[#9B2C2C]'}`}>
                    {appreciationUSD >= 0 ? '+' : ''}{appreciationPct}%
                  </p>
                </div>
                <div className="bg-white p-6">
                  <p className="text-[10px] tracking-wider uppercase text-[#888] mb-2">Editor's Call</p>
                  <span className={`inline-block text-[10px] tracking-[0.2em] uppercase border px-3 py-1.5 ${VERDICT_STYLES[piece.verdict]}`}>
                    {piece.verdict} · {piece.rating}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Price History Chart */}
      <section className="section-editorial">
        <div className="container-editorial">
          <div className="max-w-4xl mx-auto">
            <p className="overline overline-gold mb-3">Price History</p>
            <h2 className="heading-editorial text-3xl lg:text-4xl mb-10">Ten-Year Trajectory</h2>
            <div className="bg-white border border-[#E5E0D8] p-6 lg:p-10">
              <PriceChart history={piece.history || []} />
            </div>
          </div>
        </div>
      </section>

      {/* Thesis */}
      <section className="section-editorial bg-[#111] text-white">
        <div className="container-editorial">
          <div className="max-w-3xl mx-auto">
            <p className="overline text-[#C69C6D] mb-4">The Thesis</p>
            <h2 className="font-heading italic text-3xl lg:text-5xl font-light leading-tight">
              "{piece.thesis}"
            </h2>
            <p className="mt-8 overline text-white/50">— The Jewelry Refined Editorial Desk</p>
          </div>
        </div>
      </section>

      {/* CTAs */}
      <section className="py-16 lg:py-20">
        <div className="container-editorial flex flex-col sm:flex-row items-center justify-between gap-8">
          <Link to="/investments" className="group inline-flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-[#111]">
            <ArrowLeft className="w-4 h-4 text-[#C69C6D] group-hover:-translate-x-1 transition-transform" strokeWidth={1.5} />
            All Investment Picks
          </Link>
          <Link to="/drops" className="btn-editorial inline-flex items-center gap-3" data-testid="view-drops-cta">
            <BadgeCheck className="w-4 h-4" strokeWidth={1.5} />
            See Current Drops
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default InvestmentDetailPage;
